// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  ArrowLeft, Shield, ShieldCheck, ShieldAlert, Plus, Pencil, X, Info, AlertTriangle, ChevronDown,
} from "lucide-react";
import OfficialCompliancePanel from "@/components/admin/officials/OfficialCompliancePanel";
import { formatTime } from "@/lib/dates";

const money = (cents: number | null | undefined) =>
  `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const fmtDate = (s?: string | null) => (s ? new Date(s + "T12:00:00").toLocaleDateString() : "—");
const fmtTime = (s?: string | null) => formatTime(s, "—");

const emptyForm = {
  id: null as string | null,
  name: "",
  email: "",
  phone: "",
  certification_level: "",
  default_game_fee: "",
};

export default function OperatorEventOfficialsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [orgId, setOrgId] = useState<string | null>(null);
  const [eventName, setEventName] = useState("");
  const [officials, setOfficials] = useState<any[]>([]);
  const [coverage, setCoverage] = useState<any[]>([]);
  const [pay, setPay] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [savingOfficial, setSavingOfficial] = useState(false);

  const [complianceFor, setComplianceFor] = useState<any>(null);

  const loadOfficials = async (org: string) => {
    const { data, error } = await supabase.rpc("list_officials", { p_org_id: org });
    if (error) throw error;
    setOfficials(data || []);
  };

  const loadEventData = async () => {
    const [{ data: cov, error: covErr }, { data: p, error: payErr }] = await Promise.all([
      supabase.rpc("get_event_officials_coverage", { p_event_id: eventId }),
      supabase.rpc("get_official_pay_report", { p_event_id: eventId }),
    ]);
    if (covErr) throw covErr;
    if (payErr) throw payErr;
    setCoverage(cov || []);
    setPay(p || []);
  };

  const loadAll = async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: ev, error: evErr } = await supabase
        .from("events")
        .select("id, name, organization_id")
        .eq("id", eventId)
        .maybeSingle();
      if (evErr) throw evErr;
      if (!ev) throw new Error("Event not found");
      setOrgId(ev.organization_id);
      setEventName(ev.name);
      await Promise.all([loadOfficials(ev.organization_id), loadEventData()]);
    } catch (e: any) {
      setError(e?.message || "Could not load officials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [eventId]);

  const clearedOfficials = useMemo(
    () => officials.filter((o) => o.is_cleared),
    [officials],
  );

  const sortedGames = useMemo(() => {
    return [...coverage].sort((a, b) => {
      const az = Number(a.official_count) === 0 ? 0 : 1;
      const bz = Number(b.official_count) === 0 ? 0 : 1;
      if (az !== bz) return az - bz;
      return String(a.scheduled_date ?? "").localeCompare(String(b.scheduled_date ?? "")) ||
        String(a.scheduled_start_time ?? "").localeCompare(String(b.scheduled_start_time ?? ""));
    });
  }, [coverage]);

  const openNew = () => {
    setForm(emptyForm);
    setFormOpen(true);
  };
  const openEdit = (o: any) => {
    setForm({
      id: o.id,
      name: o.name ?? "",
      email: o.email ?? "",
      phone: o.phone ?? "",
      certification_level: o.certification_level ?? "",
      default_game_fee:
        o.default_game_fee_cents != null ? (o.default_game_fee_cents / 100).toFixed(2) : "",
    });
    setFormOpen(true);
  };

  const saveOfficial = async () => {
    if (!orgId) return;
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSavingOfficial(true);
    const payload: any = {
      p_org_id: orgId,
      p_name: form.name.trim(),
      p_email: form.email.trim() || null,
      p_phone: form.phone.trim() || null,
      p_certification_level: form.certification_level.trim() || null,
      p_default_game_fee_cents: form.default_game_fee
        ? Math.round(parseFloat(form.default_game_fee) * 100)
        : null,
    };
    if (form.id) payload.p_official_id = form.id;
    const { error } = await supabase.rpc("upsert_official", payload);
    setSavingOfficial(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(form.id ? "Official updated" : "Official added");
    setFormOpen(false);
    await loadOfficials(orgId);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <button
            onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}
            className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to event
          </button>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield className="h-6 w-6 text-secondary" />
            Officials
          </h1>
          {eventName && <p className="text-muted-foreground mt-1">{eventName}</p>}
        </div>

        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-3 flex items-start gap-2 text-sm">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>
              Assign officials to each game. Officials must have a cleared background check and
              SafeSport certification before they can be assigned. They'll accept or decline from
              their own portal.
            </span>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        <Tabs defaultValue="pool">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="pool">Pool</TabsTrigger>
            <TabsTrigger value="assignments">Assignments</TabsTrigger>
            <TabsTrigger value="pay">Pay</TabsTrigger>
          </TabsList>

          {/* POOL */}
          <TabsContent value="pool" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Button onClick={openNew} size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add official
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Certification</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Default fee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {officials.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No officials yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      officials.map((o) => (
                        <React.Fragment key={o.id}>
                        <TableRow>
                          <TableCell className="font-medium">{o.name}</TableCell>
                          <TableCell>{o.certification_level || "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <div>{o.phone || "—"}</div>
                            <div>{o.email || "—"}</div>
                          </TableCell>
                          <TableCell>{money(o.default_game_fee_cents)}</TableCell>
                          <TableCell>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  {o.is_cleared ? (
                                    <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100">
                                      <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Cleared
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-rose-100 text-rose-900 hover:bg-rose-100">
                                      <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Not cleared
                                    </Badge>
                                  )}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {(o.credentials ?? []).length === 0 ? (
                                  <p>No credentials on file</p>
                                ) : (
                                  <ul className="space-y-0.5">
                                    {o.credentials.map((c: any, i: number) => (
                                      <li key={i} className="capitalize">
                                        {String(c.credential_type).replace(/_/g, " ")}: {c.status}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(o)}>
                              <Pencil className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setComplianceFor((prev: any) => (prev?.id === o.id ? null : o))
                              }
                            >
                              <Shield className="h-4 w-4 mr-1" />
                              Compliance
                              <ChevronDown
                                className={`ml-1 h-3.5 w-3.5 transition ${complianceFor?.id === o.id ? "rotate-180" : ""}`}
                              />
                            </Button>
                          </TableCell>
                        </TableRow>
                        {complianceFor?.id === o.id && (
                          <TableRow className="bg-muted/20 hover:bg-muted/20">
                            <TableCell colSpan={6} className="p-3">
                              <OfficialCompliancePanel
                                officialId={o.id}
                                officialName={o.name}
                                orgId={orgId}
                                onChanged={() => orgId && loadOfficials(orgId)}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))

                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ASSIGNMENTS */}
          <TabsContent value="assignments" className="mt-6 space-y-4">
            {sortedGames.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  No scheduled games yet.
                </CardContent>
              </Card>
            ) : (
              sortedGames.map((g) => (
                <GameCard
                  key={g.game_id}
                  game={g}
                  clearedOfficials={clearedOfficials}
                  onChanged={loadEventData}
                />
              ))
            )}
          </TabsContent>

          {/* PAY */}
          <TabsContent value="pay" className="mt-6">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Official</TableHead>
                      <TableHead className="text-right">Games accepted</TableHead>
                      <TableHead className="text-right">Games pending</TableHead>
                      <TableHead className="text-right">Fees owed</TableHead>
                      <TableHead className="text-right">Projected</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pay.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No assignments yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {pay.map((r) => (
                          <TableRow key={r.official_id}>
                            <TableCell className="font-medium">{r.official_name}</TableCell>
                            <TableCell className="text-right">{r.games_accepted}</TableCell>
                            <TableCell className="text-right">{r.games_pending}</TableCell>
                            <TableCell className="text-right">
                              {money(r.total_fee_cents_accepted)}
                            </TableCell>
                            <TableCell className="text-right">
                              {money(r.total_fee_cents_all)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-semibold bg-muted/40">
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right">
                            {pay.reduce((s, r) => s + Number(r.games_accepted || 0), 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {pay.reduce((s, r) => s + Number(r.games_pending || 0), 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {money(pay.reduce((s, r) => s + Number(r.total_fee_cents_accepted || 0), 0))}
                          </TableCell>
                          <TableCell className="text-right">
                            {money(pay.reduce((s, r) => s + Number(r.total_fee_cents_all || 0), 0))}
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Official form */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{form.id ? "Edit official" : "Add official"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Certification level</Label>
                  <Input
                    value={form.certification_level}
                    onChange={(e) => setForm({ ...form, certification_level: e.target.value })}
                    placeholder="e.g. IAABO Level 2"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Default game fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.default_game_fee}
                    onChange={(e) => setForm({ ...form, default_game_fee: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button onClick={saveOfficial} disabled={savingOfficial}>
                {savingOfficial ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>



      </div>
    </TooltipProvider>
  );
}

function GameCard({ game, clearedOfficials, onChanged }: any) {
  const [officialId, setOfficialId] = useState("");
  const [position, setPosition] = useState("");
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    if (!officialId) {
      toast.error("Pick an official");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("assign_official_to_game", {
      p_game_id: game.game_id,
      p_official_id: officialId,
      p_position: position.trim() || null,
      p_game_fee_cents: fee ? Math.round(parseFloat(fee) * 100) : null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Official assigned");
    setOfficialId("");
    setPosition("");
    setFee("");
    onChanged();
  };

  const remove = async (assignmentId: string) => {
    const { error } = await supabase.rpc("remove_official_from_game", {
      p_game_official_id: assignmentId,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Official removed");
    onChanged();
  };

  const uncovered = Number(game.official_count) === 0;

  return (
    <Card className={uncovered ? "border-rose-300" : undefined}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-semibold">
              {game.team_a_name || "TBD"} vs {game.team_b_name || "TBD"}
            </p>
            <p className="text-xs text-muted-foreground">
              {fmtDate(game.scheduled_date)} · {fmtTime(game.scheduled_start_time)} ·{" "}
              {game.court_name || "No court"}
              {game.division ? ` · ${game.division}` : ""}
            </p>
          </div>
          {uncovered && (
            <Badge className="bg-rose-100 text-rose-900 hover:bg-rose-100">
              <AlertTriangle className="h-3.5 w-3.5 mr-1" /> No official
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {(game.officials ?? []).length === 0 ? (
            <span className="text-sm text-muted-foreground">No officials assigned.</span>
          ) : (
            game.officials.map((a: any) => (
              <span
                key={a.assignment_id}
                className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm"
              >
                {a.official_name}
                {a.position ? ` · ${a.position}` : ""}
                <Badge variant="secondary" className="text-[10px]">{a.status}</Badge>
                <button
                  onClick={() => remove(a.assignment_id)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Remove official"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <div className="min-w-[200px] flex-1">
            <Label className="text-xs">Assign official</Label>
            <Select value={officialId} onValueChange={setOfficialId}>
              <SelectTrigger><SelectValue placeholder="Select a cleared official" /></SelectTrigger>
              <SelectContent>
                {clearedOfficials.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No cleared officials
                  </div>
                ) : (
                  clearedOfficials.map((o: any) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <Label className="text-xs">Position</Label>
            <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="R1" />
          </div>
          <div className="w-32">
            <Label className="text-xs">Fee ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="default"
            />
          </div>
          <Button size="sm" onClick={assign} disabled={busy}>
            {busy ? "Assigning…" : "Assign"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
